import { Search, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import './Masool.css';

export default function Masool() {
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoText, setInfoText] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId.trim()) return;

    setLoading(true);
    setError('');
    setInfoText('');
    setHeaders([]);
    setRows([]);

    try {
      const infoUrl = `https://script.google.com/macros/s/AKfycbzuQ3JHzpJWZyjpIU-yd50DKLMNJHhvdbKObOZlzxNzpEqnp3cHLJ8qK_PMq7WO00tHIQ/exec?id=${encodeURIComponent(userId)}`;

      const response = await fetch(infoUrl);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setInfoText(data?.[0]?.[0] || '');
        setHeaders(data?.[1] || []);
        setRows(data?.slice(2) || []);
        setSubmitted(true);
      }
    } catch (err) {
      setError('Something went wrong while retrieving information.');
    } finally {
      setLoading(false);
    }
  };

  const getCellText = (cell) => {
  if (Array.isArray(cell)) {
    return cell
      .map((part) => {
        if (part && typeof part === 'object' && 'text' in part) {
          return part.text ?? '';
        }
        return '';
      })
      .join(' ')
      .trim();
  }

  return cell == null ? '' : String(cell).trim();
};

const parseSortableValue = (value) => {
  const text = value.trim();

  if (text === '') return '';

  const numberValue = Number(text.replace(/,/g, ''));
  if (!Number.isNaN(numberValue) && text !== '') {
    return numberValue;
  }

  return text.toLowerCase();
};

const handleSort = (columnIndex) => {
  setSortConfig((prev) => {
    if (prev.key === columnIndex) {
      return {
        key: columnIndex,
        direction: prev.direction === 'asc' ? 'desc' : 'asc'
      };
    }

    return {
      key: columnIndex,
      direction: 'asc'
    };
  });
};

const sortedRows = useMemo(() => {
  if (sortConfig.key === null) return rows;

  const sorted = [...rows].sort((a, b) => {
    const aText = getCellText(a[sortConfig.key]);
    const bText = getCellText(b[sortConfig.key]);

    const aVal = parseSortableValue(aText);
    const bVal = parseSortableValue(bText);

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return sorted;
}, [rows, sortConfig]);

const downloadCSV = () => {
  if (!headers.length) return;

  const escapeCSV = (value) => {
    const stringValue = String(value ?? '');
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const csvRows = [
    headers.map(escapeCSV).join(','),
    ...rows.map((row) =>
      row.map((cell) => escapeCSV(getCellText(cell))).join(',')
    )
  ];

  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;

  const safeId = userId.trim() || 'masool';
  link.download = `masool-${safeId}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

const renderSortIcon = (index) => {
  if (sortConfig.key !== index) {
    return <ArrowUpDown size={14} className="masool-sort-icon" />;
  }

  return sortConfig.direction === 'asc' ? (
    <ArrowUp size={14} className="masool-sort-icon active" />
  ) : (
    <ArrowDown size={14} className="masool-sort-icon active" />
  );
};
  
  const renderCellContent = (cell) => {
    if (Array.isArray(cell)) {
      return cell.map((part, index) => {
        if (part && typeof part === 'object' && 'text' in part) {
          return part.url ? (
            <a
              key={index}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="masool-link"
            >
              {part.text}
            </a>
          ) : (
            <span key={index}>{part.text}</span>
          );
        }

        return null;
      });
    }

    return cell ?? '';
  };

  const handleReset = () => {
    setUserId('');
    setLoading(false);
    setInfoText('');
    setHeaders([]);
    setRows([]);
    setError('');
    setSubmitted(false);
  };

  return (
    <div className="masool-page min-h-screen bg-white">
      {/* Hero */}
      <div
        className="relative py-20 text-white overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d66 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full border-2 border-white/30"></div>
          <div className="absolute bottom-10 left-20 w-48 h-48 rounded-full border-2 border-white/30"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 className="font-playfair text-5xl md:text-6xl font-bold mb-4">Masool</h1>
          <p className="font-poppins text-white/85 text-lg max-w-2xl">
            View your sector information by entering your Masool ITS ID.
          </p>
        </div>
      </div>

      {/* Form Card */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="masool-shell">
          {!submitted && (
            <div className="masool-form-card">
              <h2 className="masool-section-title">Enter Masool ITS ID</h2>

              <form onSubmit={handleSubmit} className="masool-form">
                <div className="masool-input-wrap">
                  <Search className="masool-input-icon" size={18} />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Enter ITS ID"
                    className="masool-input"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="masool-submit-btn"
                >
                  {loading ? 'Retrieving information...' : 'Submit'}
                </button>
              </form>

              {error && <p className="masool-error">{error}</p>}
            </div>
          )}

          {(infoText || headers.length > 0 || rows.length > 0 || error) && (
            <div className="masool-results-card">
              {infoText && <div className="masool-info-text">{infoText}</div>}
              {error && <div className="masool-error">{error}</div>}
             {headers.length > 0 && (
                  <div className="masool-toolbar">
                    <button type="button" className="masool-export-btn" onClick={downloadCSV}>
                      <Download size={16} />
                      <span>Export CSV</span>
                    </button>
                  </div>
                )}
              {headers.length > 0 && (
                <div className="masool-table-container">
                  <table className="masool-table">
                  <thead>
                    <tr>
                      {headers.map((header, index) => (
                        <th
                          key={index}
                          onClick={() => handleSort(index)}
                          className="masool-sortable-header"
                          scope="col"
                        >
                          <button type="button" className="masool-sort-button">
                            <span>{header}</span>
                            {renderSortIcon(index)}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                    <tbody>
                      {sortedRows.map((row, rowIndex) => (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'row-white' : 'row-purple'}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex}>{renderCellContent(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {submitted && (
                <div className="masool-actions">
                  <button type="button" className="masool-reset-btn" onClick={handleReset}>
                    Search Another ID
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
